import type {
  TradeOverlayCallbacks,
  TradeOverlayLineData,
  TradeOverlayLineStyle,
} from "../types";

import { klinecharts } from "../../klinecharts-client";

const OVERLAY_NAME = "vybeTradeLevel";
const LABEL_RIGHT_PADDING = 4;
const CLOSE_BUTTON_SIZE = 11;
/**
 * Approximate rendered half-width of the close `[X]` chip (font-size + side
 * padding / 2). Used to position the left status pill so it sits immediately
 * to the right of the centered close button without overlapping it.
 */
const CLOSE_BUTTON_VISUAL_HALF_WIDTH = 10;
const LEFT_STATUS_GAP = 6;
const LINE_CLOSE_BUTTON_KEY = "lineCloseButton";
/** Half-height (px) of the invisible hit-test rect that widens the drag target. */
const LINE_HIT_AREA_HALF_HEIGHT = 8;
const LINE_MIDDLE_LABEL_KEY = "lineMiddleLabel";

function getMiddleLabelText(data: TradeOverlayLineData): string {
  if (data.lineKind === "tp") return "Take Profit";
  if (data.lineKind === "sl") return "Stop Loss";

  // Entry/position label: when both TP and SL are set, surface the
  // loss-to-profit price-distance ratio (SL : TP relative to the entry) so
  // the focused trade communicates its risk/reward at a glance. Without
  // both levels we fall back to the plain "Position" label.
  const ratio = formatPositionRatio(
    data.openPrice,
    data.stopLoss,
    data.takeProfit
  );
  return ratio ? `Position Ratio ${ratio}` : "Position";
}

/**
 * Builds the "loss : profit" distance ratio as a `SL:TP` string with at
 * most one decimal place. The smaller side is normalized to `1`, e.g. a
 * 1-pip stop and 3-pip target render as `1:3`, a 1-pip stop and 2.3-pip
 * target as `1:2.3`, and a 3.4-pip stop with a 1-pip target as `3.4:1`.
 * Returns `null` when either side is missing or zero.
 */
function formatPositionRatio(
  openPrice: number,
  stopLoss: number | null,
  takeProfit: number | null
): string | null {
  if (!stopLoss || !takeProfit) return null;

  const lossDistance = Math.abs(openPrice - stopLoss);
  const profitDistance = Math.abs(takeProfit - openPrice);

  if (!Number.isFinite(lossDistance) || !Number.isFinite(profitDistance)) {
    return null;
  }
  if (lossDistance <= 0 || profitDistance <= 0) {
    return null;
  }

  if (lossDistance <= profitDistance) {
    return `1:${formatRatioSide(profitDistance / lossDistance)}`;
  }

  return `${formatRatioSide(lossDistance / profitDistance)}:1`;
}

function formatRatioSide(value: number): string {
  // Round to one decimal, then drop the decimal when it's `.0` so values
  // like 3 render as "3" instead of "3.0" while 2.3 stays as "2.3".
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
}

function getLineStyle(lineStyle: TradeOverlayLineStyle) {
  if (lineStyle === "dotted") {
    return { style: "dashed", dashedValue: [2, 4] };
  }

  if (lineStyle === "dashed") {
    return { style: "dashed", dashedValue: [4, 4] };
  }

  return { style: "solid" };
}

function createPointFigures(params: any) {
  const { coordinates, bounding, overlay, yAxis } = params;
  const data = overlay.extendData as TradeOverlayLineData | undefined;
  const point = coordinates?.[0];

  if (!data || !point) {
    return [];
  }

  const lineStyle = getLineStyle(data.lineStyle);
  const lineStartX = Math.max(0, Math.min(point.x ?? 0, bounding.width));
  const labelY = point.y + (data.labelOffsetY || 0);
  const figures: any[] = [];

  const zoneFigure = createRiskRewardZoneFigure({
    data,
    yAxis,
    lineStartX,
    lineEndX: bounding.width,
  });

  if (zoneFigure) {
    figures.push(zoneFigure);
  }

  // Invisible widened hit target for draggable lines (TP/SL/pending entry):
  // a plain 2px-deviation line is nearly impossible to grab, so an unpainted
  // rect (~16px tall) centered on the price line takes part in hit-testing
  // and routes clicks/drags to the same overlay handlers.
  if (canDragLine(data)) {
    figures.push({
      key: "tradeLevelHitArea",
      type: "rect",
      attrs: {
        x: lineStartX,
        y: point.y - LINE_HIT_AREA_HALF_HEIGHT,
        width: Math.max(0, bounding.width - lineStartX),
        height: LINE_HIT_AREA_HALF_HEIGHT * 2,
      },
      styles: {
        style: "fill",
        color: "rgba(0, 0, 0, 0)",
      },
      ignoreEvent: false,
    });
  }

  figures.push({
    key: "tradeLevelLine",
    type: "line",
    attrs: {
      coordinates: [
        { x: lineStartX, y: point.y },
        { x: bounding.width, y: point.y },
      ],
    },
    styles: {
      ...lineStyle,
      color: data.color,
      size: data.lineWidth,
    },
  });

  // [X] close button + left status are now rendered for entry, tp, AND sl
  // lines, anchored at the trade's open time so they sit on the line itself.
  const closeButtonX = lineStartX;

  if (!data.readOnly) figures.push({
    key: LINE_CLOSE_BUTTON_KEY,
    type: "text",
    attrs: {
      x: closeButtonX,
      y: labelY,
      text: "X",
      align: "center",
      baseline: "middle",
    },
    styles: {
      style: "fill",
      color: data.textColor,
      size: CLOSE_BUTTON_SIZE,
      family: "Arial",
      weight: "bold",
      backgroundColor: data.color,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 2,
      paddingBottom: 2,
      borderRadius: 3,
      borderStyle: "solid",
      borderColor: data.color,
      borderSize: 1,
      cursor: "pointer",
    },
    ignoreEvent: false,
  });

  if (data.leftStatus) {
    figures.push({
      key: "lineLeftStatus",
      type: "text",
      attrs: {
        x: closeButtonX + CLOSE_BUTTON_VISUAL_HALF_WIDTH + LEFT_STATUS_GAP,
        y: labelY,
        text: data.leftStatus,
        align: "left",
        baseline: "middle",
      },
      styles: getLabelStyles(data),
      ignoreEvent: true,
    });
  }

  // Middle "Position / Take Profit / Stop Loss" handle for the focused trade.
  // Centered horizontally between the line's start and the chart's right edge,
  // it doubles as a visible drag grip for TP and SL — `ignoreEvent: false`
  // lets klinecharts route press-move events on the chip to the overlay's
  // drag handlers (the overlay-level `lock` flag controls whether dragging is
  // actually allowed; see `canDragLine`).
  if (data.selected) {
    const middleX = (lineStartX + bounding.width) / 2;
    figures.push({
      key: LINE_MIDDLE_LABEL_KEY,
      type: "text",
      attrs: {
        x: middleX,
        y: labelY,
        text: getMiddleLabelText(data),
        align: "center",
        baseline: "middle",
      },
      styles: getLabelStyles(data),
      ignoreEvent: false,
    });
  }

  figures.push({
    key: "tradeLevelLabel",
    type: "text",
    attrs: {
      x: bounding.width - LABEL_RIGHT_PADDING,
      y: labelY,
      text: data.label,
      align: "right",
      baseline: "middle",
    },
    styles: getLabelStyles(data),
    ignoreEvent: false,
  });

  return figures;
}

function createRiskRewardZoneFigure({
  data,
  yAxis,
  lineStartX,
  lineEndX,
}: {
  data: TradeOverlayLineData;
  yAxis?: { convertToPixel?: (value: number) => number };
  lineStartX: number;
  lineEndX: number;
}) {
  if (!data.selected || (data.lineKind !== "tp" && data.lineKind !== "sl")) {
    return null;
  }

  if (!yAxis?.convertToPixel || lineEndX <= lineStartX) {
    return null;
  }

  const entryY = yAxis.convertToPixel(data.openPrice);
  const targetY = yAxis.convertToPixel(data.price);

  if (!Number.isFinite(entryY) || !Number.isFinite(targetY)) {
    return null;
  }

  const height = Math.abs(targetY - entryY);
  if (height < 1) {
    return null;
  }

  return {
    key: data.lineKind === "tp" ? "takeProfitZone" : "stopLossZone",
    type: "rect",
    attrs: {
      x: lineStartX,
      y: Math.min(entryY, targetY),
      width: lineEndX - lineStartX,
      height,
    },
    styles: {
      style: "fill",
      color: toRgba(data.color, 0.18),
    },
    ignoreEvent: true,
  };
}

function getLabelStyles(data: TradeOverlayLineData) {
  // Entry labels (open + pending) and any focused/selected trade use solid
  // backgrounds with the label text color. Non-selected TP/SL labels use a
  // 16% tinted background with the line color as text — that subtle look is
  // what visually separates a focused trade from the rest of the overlays.
  const useSolidBackground = data.lineKind === "entry" || data.selected;
  const backgroundColor = useSolidBackground
    ? data.color
    : toRgba(data.color, 0.16);
  const textColor = useSolidBackground ? data.textColor : data.color;

  return {
    style: "fill",
    color: textColor,
    size: 10,
    family: "Arial",
    weight: "bold",
    backgroundColor,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 3,
    borderStyle: "solid",
    borderColor: backgroundColor,
    borderSize: 1,
  };
}

function getPriceText(data: TradeOverlayLineData): string {
  if (data.lineKind === "tp") {
    return `TP ${data.price.toFixed(Math.max(0, Math.min(data.digits || 5, 8)))}`;
  }

  if (data.lineKind === "sl") {
    return `SL ${data.price.toFixed(Math.max(0, Math.min(data.digits || 5, 8)))}`;
  }

  return data.price.toFixed(Math.max(0, Math.min(data.digits || 5, 8)));
}

function createYAxisFigures(params: any) {
  const { coordinates, overlay, yAxis } = params;
  const data = overlay.extendData as TradeOverlayLineData | undefined;

  if (!data) {
    return [];
  }

  const y = coordinates?.[0]?.y ?? yAxis.convertToPixel(data.price);

  return [
    {
      key: "tradeLevelPriceAxisLabel",
      type: "text",
      attrs: {
        x: 0,
        y,
        text: getPriceText(data),
        align: "left",
        baseline: "middle",
      },
      styles: getPriceAxisLabelStyles(data),
    },
  ];
}

function getPriceAxisLabelStyles(data: TradeOverlayLineData) {
  return {
    style: "fill",
    color: data.textColor,
    size: 10,
    family: "Arial",
    weight: "bold",
    backgroundColor: data.backgroundColor,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 2,
    paddingBottom: 2,
    borderRadius: 2,
    borderStyle: "solid",
    borderColor: data.backgroundColor,
    borderSize: 1,
  };
}

function toRgba(color: string, opacity: number): string {
  if (color.startsWith("rgba(")) {
    return color.replace(
      /rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/,
      `rgba($1,$2,$3,${opacity})`
    );
  }

  if (!color.startsWith("#")) {
    return color;
  }

  const hex = color.slice(1);
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((part) => part + part)
          .join("")
      : hex;

  if (fullHex.length !== 6) {
    return color;
  }

  const value = Number.parseInt(fullHex, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
}

function getDraggedPrice(event: any): number | null {
  const pointValue = event?.overlay?.points?.[0]?.value;
  if (Number.isFinite(pointValue)) {
    return pointValue;
  }

  if (event?.yAxis && event?.y !== undefined) {
    const axisValue = event.yAxis.convertFromPixel(event.y);
    return Number.isFinite(axisValue) ? axisValue : null;
  }

  return null;
}

function canDragPendingEntry(data?: TradeOverlayLineData): boolean {
  return Boolean(data?.isPending && data.lineKind === "entry");
}

function canDragLine(data?: TradeOverlayLineData): data is TradeOverlayLineData {
  if (!data || data.readOnly) return false;
  if (data.lineKind === "tp" || data.lineKind === "sl") return true;
  return canDragPendingEntry(data);
}

function dispatchClose(event: any) {
  const overlay = event.overlay;
  const data = overlay?.extendData as TradeOverlayLineData | undefined;
  if (!data || data.readOnly) return;

  const ticket = data.ticket;
  if (data.lineKind === "tp") {
    overlay.onTpClose?.(ticket);
  } else if (data.lineKind === "sl") {
    overlay.onSlClose?.(ticket);
  } else {
    overlay.onLineClose?.(ticket);
  }
}

function dispatchDragStart(event: any) {
  const overlay = event.overlay;
  const data = overlay?.extendData as TradeOverlayLineData | undefined;
  if (!data) return;

  const ticket = data.ticket;
  if (data.lineKind === "tp") {
    overlay.onTpDragStart?.(ticket);
  } else if (data.lineKind === "sl") {
    overlay.onSlDragStart?.(ticket);
  } else if (canDragPendingEntry(data)) {
    overlay.onPendingEntryDragStart?.(ticket);
  }
}

function dispatchDragEnd(
  event: any,
  data: TradeOverlayLineData,
  price: number
) {
  const overlay = event.overlay;
  const ticket = data.ticket;
  if (data.lineKind === "tp") {
    overlay.onTpDragEnd?.(ticket);
    overlay.onTpPriceChange?.(ticket, price);
  } else if (data.lineKind === "sl") {
    overlay.onSlDragEnd?.(ticket);
    overlay.onSlPriceChange?.(ticket, price);
  } else if (canDragPendingEntry(data)) {
    overlay.onPendingEntryDragEnd?.(ticket);
    overlay.onPendingEntryPriceChange?.(ticket, price);
  }
}

function applyDragPriceToData(data: TradeOverlayLineData, price: number) {
  data.price = price;
  if (data.lineKind === "entry") {
    data.openPrice = price;
  }
}

function getOriginalPrice(data: TradeOverlayLineData): number {
  return data.lineKind === "entry" ? data.openPrice : data.price;
}

/**
 * Returns true when the dragged price is effectively unchanged from the
 * starting price (within half a price-display tick). Used to skip the
 * `modifyOrder` round-trip on a pure click that didn't move the line.
 */
function isPriceUnchanged(
  startPrice: number | undefined,
  endPrice: number,
  digits: number
): boolean {
  if (!Number.isFinite(startPrice ?? NaN)) return false;
  const tickEpsilon = Math.pow(10, -Math.max(digits, 0)) / 2;
  return Math.abs(endPrice - (startPrice as number)) < tickEpsilon;
}

export const tradeLevelOverlay = {
  name: OVERLAY_NAME,
  totalStep: 1,
  lock: true,
  visible: true,
  zLevel: 9,
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: true,
  mode: "normal" as const,
  modeSensitivity: 8,
  createPointFigures,
  createYAxisFigures,
  onClick: (event: any) => {
    event.overlay?.onTradeInteraction?.();

    if (event.figureKey === LINE_CLOSE_BUTTON_KEY) {
      dispatchClose(event);
      return true;
    }

    const ticket = event.overlay?.extendData?.ticket;
    event.overlay?.onLineClick?.(ticket);
    return false;
  },
  onDoubleClick: (event: any) => {
    event.overlay?.onTradeInteraction?.();

    const ticket = event.overlay?.extendData?.ticket;
    event.overlay?.onLineDoubleClick?.(ticket);
    return true;
  },
  onPressedMoveStart: (event: any) => {
    event.overlay?.onTradeInteraction?.();

    if (event.figureKey === LINE_CLOSE_BUTTON_KEY) {
      return true;
    }

    const overlay = event.overlay;
    const data = overlay?.extendData as TradeOverlayLineData | undefined;
    if (data && canDragLine(data)) {
      // Capture the starting price so onPressedMoveEnd can detect "click with
      // no drag" and skip the modifyOrder round-trip.
      overlay._dragStartPrice = getOriginalPrice(data);
    }

    dispatchDragStart(event);
    return false;
  },
  onPressedMoving: (event: any) => {
    if (event.figureKey === LINE_CLOSE_BUTTON_KEY) {
      return true;
    }

    const overlay = event.overlay;
    const data = overlay?.extendData as TradeOverlayLineData | undefined;
    const price = getDraggedPrice(event);

    if (canDragLine(data) && price !== null) {
      applyDragPriceToData(data, price);
      // Live recompute the left-side P&L/pips so the user sees what they
      // are setting as they drag. The closure stored on the overlay
      // captures the latest pnl context (account currency + cross-rate
      // quotes) at the time `assignOverlayCallbacks` was last called.
      const recompute = overlay.recomputeLeftStatus as
        | ((p: number) => string | undefined)
        | undefined;
      if (recompute) {
        const next = recompute(price);
        if (next !== undefined) {
          data.leftStatus = next;
        }
      }

      // Notify the manager so the trade's *entry* overlay can refresh its
      // "Position Ratio X:Y" middle label with the dragged TP/SL price.
      // Each line is a separate overlay, so without this hop the entry
      // line keeps the pre-drag ratio until the modify round-trip lands.
      if (data.lineKind === "tp" || data.lineKind === "sl") {
        overlay.onLiveTpSlChange?.(data.ticket, data.lineKind, price);
      }
    }

    return false;
  },
  onPressedMoveEnd: (event: any) => {
    if (event.figureKey === LINE_CLOSE_BUTTON_KEY) {
      return true;
    }

    const overlay = event.overlay;
    const data = overlay?.extendData as TradeOverlayLineData | undefined;
    const price = getDraggedPrice(event);
    const startPrice: number | undefined = overlay?._dragStartPrice;
    overlay._dragStartPrice = undefined;

    if (!canDragLine(data) || price === null) {
      return false;
    }

    // Pure click (no real movement) — restore the original price and skip
    // the modify round-trip. Without this guard klinecharts fires
    // onPressedMoveEnd for any mouse-up on an unlocked overlay, which would
    // send a redundant modifyOrder for every click on TP/SL/pending entry.
    if (isPriceUnchanged(startPrice, price, data.digits)) {
      const original = startPrice as number;
      data.price = original;
      if (data.lineKind === "entry") {
        data.openPrice = original;
      }
      // Still notify the drag-tracking ref so the per-tick overlay rebuild
      // unblocks if onPressedMoveStart marked this line as "in drag".
      if (data.lineKind === "tp") {
        overlay.onTpDragEnd?.(data.ticket);
      } else if (data.lineKind === "sl") {
        overlay.onSlDragEnd?.(data.ticket);
      } else if (canDragPendingEntry(data)) {
        overlay.onPendingEntryDragEnd?.(data.ticket);
      }
      return false;
    }

    applyDragPriceToData(data, price);
    dispatchDragEnd(event, data, price);

    return false;
  },
  onMouseEnter: () => {
    document.querySelectorAll("canvas").forEach((canvas) => {
      canvas.style.cursor = "pointer";
    });
    return false;
  },
  onMouseLeave: () => {
    document.querySelectorAll("canvas").forEach((canvas) => {
      canvas.style.removeProperty("cursor");
    });
    return false;
  },
};

export function registerTradeLevelOverlay() {
  if (!klinecharts) {
    return;
  }

  try {
    const supportedOverlays = klinecharts.getSupportOverlays?.();
    if (supportedOverlays?.includes?.(OVERLAY_NAME)) {
      return;
    }

    klinecharts.registerOverlay(tradeLevelOverlay);
  } catch (error) {
    console.error("Failed to register trade level overlay:", error);
  }
}

export function createTradeLevelOverlay(
  chartInstance: any,
  lineData: TradeOverlayLineData,
  callbacks: TradeOverlayCallbacks
) {
  if (!chartInstance) {
    return null;
  }

  const overlayId = chartInstance.createOverlay({
    name: OVERLAY_NAME,
    lock: !canDragLine(lineData),
    points: [{ timestamp: lineData.openTime, value: lineData.price }],
    extendData: lineData,
  });

  if (overlayId) {
    assignOverlayCallbacks(chartInstance, overlayId, callbacks);
  }

  return overlayId as string | null;
}

export function updateTradeLevelOverlay(
  chartInstance: any,
  overlayId: string,
  lineData: TradeOverlayLineData,
  callbacks?: TradeOverlayCallbacks
) {
  if (!chartInstance || !overlayId) {
    return;
  }

  chartInstance.overrideOverlay({
    id: overlayId,
    lock: !canDragLine(lineData),
    points: [{ timestamp: lineData.openTime, value: lineData.price }],
    extendData: lineData,
  });

  if (callbacks) {
    assignOverlayCallbacks(chartInstance, overlayId, callbacks);
  }
}

function assignOverlayCallbacks(
  chartInstance: any,
  overlayId: string,
  callbacks: TradeOverlayCallbacks
) {
  const overlay = chartInstance.getOverlayById?.(overlayId);
  if (!overlay) {
    return;
  }

  overlay.onLineClick = callbacks.onLineClick;
  overlay.onLineDoubleClick = callbacks.onLineDoubleClick;
  overlay.onLineClose = callbacks.onLineClose;
  overlay.onPendingEntryDragStart = callbacks.onPendingEntryDragStart;
  overlay.onPendingEntryDragEnd = callbacks.onPendingEntryDragEnd;
  overlay.onPendingEntryPriceChange = callbacks.onPendingEntryPriceChange;
  overlay.onTpClose = callbacks.onTpClose;
  overlay.onSlClose = callbacks.onSlClose;
  overlay.onTpDragStart = callbacks.onTpDragStart;
  overlay.onTpDragEnd = callbacks.onTpDragEnd;
  overlay.onTpPriceChange = callbacks.onTpPriceChange;
  overlay.onSlDragStart = callbacks.onSlDragStart;
  overlay.onSlDragEnd = callbacks.onSlDragEnd;
  overlay.onSlPriceChange = callbacks.onSlPriceChange;
  overlay.onTradeInteraction = callbacks.onTradeInteraction;
  overlay.recomputeLeftStatus = callbacks.recomputeLeftStatus;
  overlay.onLiveTpSlChange = callbacks.onLiveTpSlChange;
}
